import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { SurveysFilterParams, SurveysFilters, SurveysQueryRules } from '../model/interfaces'

import { SurveyStatus } from '@brighterly/lib-core-types/survey/enums'

import { getFirstQueryValue, isId, normalizeArrayQuery, queryToDateTimeBounds, toIdValue } from '@shared/lib/routes'

/** Array filters that links from app-main encode as `key[0]=…`. */
export const SURVEYS_ARRAY_QUERY_KEYS: readonly string[] = ['status', 'tutor']

/** Contract §1: `q` is at most 255 characters. */
export const SURVEYS_SEARCH_MAX_LENGTH = 255

const SURVEYS_FILTER_QUERY_KEYS: readonly string[] = ['q', 'from', 'to', 'name', 'status', 'source', 'tutor']
const SURVEY_STATUSES: readonly string[] = Object.values(SurveyStatus)

const toKnownKey = (value: unknown, known: Record<string, string> | undefined): string | null => {
  const raw = getFirstQueryValue(value)

  return raw === null || !known || Object.keys(known).includes(raw) ? raw : null
}

export const isSurveyStatus = (value: string): value is SurveyStatus => SURVEY_STATUSES.includes(value)

export const emptySurveysFilters = (): SurveysFilters => ({
  search: null,
  from: null,
  to: null,
  type: null,
  statuses: [],
  source: null,
  tutorIds: [],
})

export const queryToSurveysFilters = (query: Record<string, unknown>, rules: SurveysQueryRules = {}): SurveysFilters => ({
  search: getFirstQueryValue(query.q)?.slice(0, SURVEYS_SEARCH_MAX_LENGTH) ?? null,
  ...queryToDateTimeBounds(query.from, query.to),
  type: toKnownKey(query.name, rules.lists?.name),
  statuses: (normalizeArrayQuery(query.status) ?? []).filter(isSurveyStatus),
  source: toKnownKey(query.source, rules.lists?.source),
  tutorIds: rules.canFilterByTutor === false
    ? []
    : (normalizeArrayQuery(query.tutor) ?? []).map(toIdValue).filter(isId),
})

export const getSurveysFilterParams = (filters: SurveysFilters): SurveysFilterParams => ({
  q: filters.search || undefined,
  from: filters.from || undefined,
  to: filters.to || undefined,
  name: filters.type || undefined,
  status: filters.statuses.length ? filters.statuses : undefined,
  source: filters.source || undefined,
  tutor: filters.tutorIds.length ? filters.tutorIds : undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const surveysFiltersToQuery = (filters: SurveysFilters): LocationQueryRaw => {
  const params = getSurveysFilterParams(filters)

  return {
    ...params,
    tutor: params.tutor?.map(String),
  }
}

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

/**
 * The query with its filters written the way `filters` reads them, or `null` when it already is. A value
 * the filters dropped (unknown type, lone `to`, hidden tutor filter…) must leave the URL too, or the URL
 * and the controls would disagree.
 */
export const getCanonicalSurveysQuery = (query: LocationQuery, filters: SurveysFilters): LocationQueryRaw | null => {
  const canonical = surveysFiltersToQuery(filters)
  const isCanonical = SURVEYS_FILTER_QUERY_KEYS.every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}
